import { ErrorFlagStatusValue } from '../value-objects/ErrorFlagStatusValue';
import {
  ERROR_FLAG_DISPLAY_CONFIG,
  type ErrorDisplayItem,
} from '@/features/panels/constants/panelDetailTab.constants';

// ========== 型定義 ==========

/** APIから返されるエラーフラグの値オブジェクト */
interface ErrorApiValues {
  readonly [fieldName: string]: boolean | number | null;
}

/** APIから返されるエラーデータの生データ型 */
export interface ErrorDataApiData {
  readonly panelId: number;
  readonly receivedAt: string | null;
  readonly errors: ErrorApiValues | null;
}

interface PanelErrorDataConstructorParams {
  readonly panelId: number;
  readonly receivedAt: Date | null;
  readonly errorFlagStatuses: readonly ErrorFlagStatusValue[];
}

// ========== エンティティ ==========

/**
 * エラーデータエンティティ
 *
 * APIレスポンスからファクトリメソッドで生成し、
 * ERROR_FLAG_DISPLAY_CONFIGの定義数に対応するErrorFlagStatusValue値オブジェクトを保持する。
 */
export class PanelErrorData {
  private readonly panelId: number;
  private readonly receivedAt: Date | null;
  private readonly errorFlagStatuses: readonly ErrorFlagStatusValue[];

  private constructor(params: PanelErrorDataConstructorParams) {
    this.panelId = params.panelId;
    this.receivedAt = params.receivedAt;
    this.errorFlagStatuses = params.errorFlagStatuses;
  }

  /**
   * APIレスポンスからエンティティを生成
   *
   * ERROR_FLAG_DISPLAY_CONFIG を参照し、
   * 各フィールドに対応するErrorFlagStatusValueを生成する。
   */
  public static createFromApiResponse(apiData: ErrorDataApiData): PanelErrorData {
    const errorValues = apiData.errors;

    const errorFlagStatuses = ERROR_FLAG_DISPLAY_CONFIG.map(
      (config) => {
        const errorCodeRawValue = config.errorCodeFieldName !== null && errorValues !== null
          ? errorValues[config.errorCodeFieldName]
          : null;
        const errorCode = typeof errorCodeRawValue === 'number' ? errorCodeRawValue : null;

        return ErrorFlagStatusValue.create({
          displayName: config.displayName,
          isActive: errorValues !== null
            ? (errorValues[config.fieldName] === true)
            : false,
          errorCode,
        });
      }
    );

    return new PanelErrorData({
      panelId: apiData.panelId,
      receivedAt: apiData.receivedAt !== null ? new Date(apiData.receivedAt) : null,
      errorFlagStatuses,
    });
  }

  public getPanelId(): number {
    return this.panelId;
  }

  public getReceivedAt(): Date | null {
    return this.receivedAt;
  }

  /**
   * プレゼンテーションモデルに変換
   */
  public toPresentationModel(): readonly ErrorDisplayItem[] {
    return this.errorFlagStatuses.map((errorFlagStatus) => errorFlagStatus.toPresentationModel());
  }
}
